import { Injectable, inject, Type, InjectionToken, Injector, effect, EffectRef, Signal, untracked, isSignal } from '@angular/core';

/**
 * Ref - Servicio para acceder a dependencias desde Notifiers
 * Equivalente al 'ref' de Riverpod en Flutter
 *
 * 3 métodos:
 *   ref.watch(signalOrProvider)       — reactivo, crea dependencia
 *   ref.read(signalOrProvider)        — one-shot, sin dependencia
 *   ref.listen(fn)                    — crear effect (lee múltiples signals)
 *   ref.listen(signal, callback)      — escucha un signal con prev/next
 */
@Injectable({ providedIn: 'root' })
export class Ref {
  private readonly injector = inject(Injector);

  /**
   * Lee un signal o provider REACTIVAMENTE.
   * - Signal: crea dependencia en el effect (tracked)
   * - Provider/Service: retorna instancia via DI
   * Equivalente a Riverpod's ref.watch(provider).
   */
  watch<T>(arg: Signal<T> | Type<T> | InjectionToken<T>): T {
    if (isSignal(arg)) return arg();
    return this.injector.get(arg as Type<T> | InjectionToken<T>);
  }

  /**
   * Lee un signal o provider SIN crear dependencia (one-shot).
   * - Signal: lee valor actual sin trackear
   * - Provider/Service: retorna instancia via DI
   * Equivalente a Riverpod's ref.read(provider).
   */
  read<T>(arg: Signal<T> | Type<T> | InjectionToken<T>): T {
    if (isSignal(arg)) return untracked(() => arg());
    return this.injector.get(arg as Type<T> | InjectionToken<T>);
  }

  /**
   * listen(fn) — Crea un effect (para leer múltiples signals).
   * listen(signal, cb) — Escucha un signal con callback(prev, next).
   * Equivalente a Riverpod's ref.listen(provider, callback).
   */
  listen(fn: () => void): EffectRef;
  listen<T>(signal: Signal<T>, callback: (previous: T, next: T) => void): EffectRef;
  listen(arg: any, callback?: any): EffectRef {
    if (typeof arg === 'function') {
      return effect(arg, { injector: this.injector });
    }
    let prev = untracked(() => arg());
    return effect(() => {
      const next = arg();
      callback(prev, next);
      prev = next;
    }, { injector: this.injector });
  }
}
